import { useEffect, useState } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { DropCapSelectionSummary } from '@/lib/text/types/drop-cap-selection-summary'
import { summarizeDropCapSelection } from '@/lib/text/drop-cap'
import { useFonts } from '@/providers/font'
import { alignmentToUiValue, resolveUniformAlignment } from '../helpers/alignment'
import {
  type FontFamilyDisplay,
  resolveEmptyFamilyDisplay,
  resolveLiveEditFamilyDisplay,
  resolveParagraphFamilyDisplay,
} from '../helpers/font-family-display'
import { getFirstRunProperty, isRunPropertyMixed } from '../helpers/run-properties'
import type { AlignmentUiValue } from '../types/alignment-ui-value'
import type { TypePanelProps } from '../types/type-panel-props'

type SelectionSyncProps = Pick<
  TypePanelProps,
  | 'selectedShapeTextParagraphs'
  | 'spreadId'
  | 'defaultFontSize'
  | 'getEditingShapeTextId'
  | 'getEditingShapeTextSelectionStyle'
  | 'getEditingShapeTextKerningState'
>

/** The stable (never re-created) display methods each field hook exposes to the sync effect. */
interface SelectionSyncFields {
  resolvedDefaultFontFamilyId: number | undefined
  textPanelRevision: number
  shapeTextEditRevision: number
  syncFontSizeFromLiveEdit: (style: TextRunStyleUpdate) => void
  showFontSizeSelection: (mixed: boolean, size: unknown) => void
  resetFontSize: (size: number) => void
  setUnderline: (underline: boolean) => void
  setAlignment: (alignment: AlignmentUiValue | null) => void
  syncKerningFromLiveEdit: (
    kerningState: { available: boolean; value: number } | null,
    styleMixedKerning: boolean
  ) => void
  syncKerningFromParagraphs: (mixed: boolean) => void
  resetKerning: () => void
  setLiveEditingCollapsedCaret: (collapsed: boolean) => void
  syncTrackingFromLiveEdit: (style: TextRunStyleUpdate | null | undefined) => void
  syncTrackingFromParagraphs: (mixed: boolean, value: unknown) => void
  resetTracking: () => void
  showFamilyDisplay: (display: FontFamilyDisplay) => void
  syncLeadingFromStoredValue: (stored: string | undefined, mixed: boolean, size: number) => void
  trackLeadingEditingSession: (editingShapeTextId: string | null) => void
  resyncLeadingFromLiveEdit: (
    style: TextRunStyleUpdate | null | undefined,
    defaultSize: number | undefined
  ) => void
  resyncLeadingFromParagraphs: (paragraphs: Paragraph[]) => void
  clearLeading: () => void
  applyDropCapSummary: (summary: DropCapSelectionSummary) => void
  resetDropCap: () => void
}

/**
 * Syncs local state from the selected shape's text; clears typography when nothing is selected or
 * the spread changed away. Re-reads the caret/selection style as the caret moves or the selection
 * changes during inline editing.
 */
export function useSelectionSync({
  selectedShapeTextParagraphs,
  spreadId,
  defaultFontSize,
  getEditingShapeTextId,
  getEditingShapeTextSelectionStyle,
  getEditingShapeTextKerningState,
  resolvedDefaultFontFamilyId,
  textPanelRevision,
  shapeTextEditRevision,
  syncFontSizeFromLiveEdit,
  showFontSizeSelection,
  resetFontSize,
  setUnderline,
  setAlignment,
  syncKerningFromLiveEdit,
  syncKerningFromParagraphs,
  resetKerning,
  setLiveEditingCollapsedCaret,
  syncTrackingFromLiveEdit,
  syncTrackingFromParagraphs,
  resetTracking,
  showFamilyDisplay,
  syncLeadingFromStoredValue,
  trackLeadingEditingSession,
  resyncLeadingFromLiveEdit,
  resyncLeadingFromParagraphs,
  clearLeading,
  applyDropCapSummary,
  resetDropCap,
}: SelectionSyncProps & SelectionSyncFields) {
  const {
    ensureFamilyFacesLoaded,
    getVariantByFontId,
    getFamilyById,
    pickDefaultFontIdForFamilyId,
  } = useFonts()

  // Re-read caret/selection style as the caret moves or selection changes during inline editing.
  const [editSelectionTick, setEditSelectionTick] = useState(0)

  useEffect(() => {
    const onSelectionChange = () => {
      if (getEditingShapeTextId?.()) {
        setEditSelectionTick((n) => n + 1)
      }
    }

    document.addEventListener('selectionchange', onSelectionChange)

    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [getEditingShapeTextId])

  useEffect(() => {
    const showFamily = (display: FontFamilyDisplay) => {
      showFamilyDisplay(display)
      if (display.ensureFamilyId != null) void ensureFamilyFacesLoaded(display.ensureFamilyId)
    }

    // Shape text editing: short-circuit to prevent font-loading dep re-runs from reverting
    // panel state back to the paragraphs' (stale) font when the user just selected a new one.
    // Gate on the editing id, not on `shapeEditStyle` truthiness — `getEditingShapeTextSelectionStyle`
    // legitimately returns null whenever the run at the caret has no populated style fields (e.g.
    // right after Kerning isolates a single character into its own run), which must not be
    // misread as "edit mode exited" and fall through to the Select-Tool branch below, which force-
    // disables Kerning. Mirrors the text-box path, which hoists kerningAvailable/kerningAtCaret out
    // of the general style object for the same reason.
    const editingShapeTextId = getEditingShapeTextId?.()
    const shapeEditStyle = editingShapeTextId ? getEditingShapeTextSelectionStyle?.() : null

    // See `trackEditingSession` in `use-leading-field.ts`.
    trackLeadingEditingSession(editingShapeTextId ?? null)

    if (editingShapeTextId) {
      // Tracking's collapsed-caret gate (see `liveEditingCollapsedCaret`'s own doc comment): only a
      // genuine range selection sets `isRangeSelection`, so its absence — including the rare edge
      // case where `shapeEditStyle` itself comes back null — means the caret is collapsed.
      setLiveEditingCollapsedCaret(!shapeEditStyle?.isRangeSelection)

      if (shapeEditStyle) {
        syncFontSizeFromLiveEdit(shapeEditStyle)

        if (typeof shapeEditStyle.underline === 'boolean') setUnderline(shapeEditStyle.underline)
      }

      syncKerningFromLiveEdit(
        getEditingShapeTextKerningState?.() ?? null,
        shapeEditStyle?.mixedKerning ?? false
      )
      syncTrackingFromLiveEdit(shapeEditStyle)
      showFamily(resolveLiveEditFamilyDisplay(shapeEditStyle, getVariantByFontId))
      resyncLeadingFromLiveEdit(shapeEditStyle, defaultFontSize)

      if (shapeEditStyle?.alignment != null) {
        setAlignment(alignmentToUiValue(shapeEditStyle.alignment))
      }

      // Drop cap is paragraph-level, like alignment above — but unlike alignment, a real (non-
      // collapsed) text-tool selection CAN disagree across paragraphs, same as the whole-box
      // Select-Tool branch below. `dropCapSummary` already resolved that (see
      // `selectionMixedStyle` in `use-shape-text-style.ts`) — a collapsed caret's own summary is
      // always a clean single-paragraph result, so this needs no branch of its own for that case.
      if (shapeEditStyle?.dropCapSummary) {
        applyDropCapSummary(shapeEditStyle.dropCapSummary)
      }

      return
    }

    const paras = selectedShapeTextParagraphs ?? null

    // Not live-editing — no caret to be collapsed or not. The Select-Tool whole-box path below
    // gates Tracking through `hasTextSelectionTarget` alone, same as before.
    setLiveEditingCollapsedCaret(false)

    if (!paras) {
      resetFontSize(defaultFontSize ?? DEFAULT_POINT_SIZE)
      setUnderline(false)
      resetKerning()
      resetTracking()
      setAlignment(null)
      showFamily(resolveEmptyFamilyDisplay(resolvedDefaultFontFamilyId))
      clearLeading()
      resetDropCap()
      return
    }

    const psMixed = isRunPropertyMixed(paras, 'pointSize')
    const ps = getFirstRunProperty(paras, 'pointSize')

    showFontSizeSelection(psMixed, ps)

    const ul = getFirstRunProperty(paras, 'underline')

    if (typeof ul === 'boolean') setUnderline(ul)

    syncKerningFromParagraphs(isRunPropertyMixed(paras, 'kerning'))
    syncTrackingFromParagraphs(
      isRunPropertyMixed(paras, 'tracking'),
      getFirstRunProperty(paras, 'tracking')
    )
    setAlignment(resolveUniformAlignment(paras))

    // Whole-box (Select-Tool, no caret) drop cap summary — see `applyDropCapSummary`.
    applyDropCapSummary(summarizeDropCapSelection(paras))

    showFamily(resolveParagraphFamilyDisplay(paras, getVariantByFontId))
    resyncLeadingFromParagraphs(paras)
  }, [
    spreadId,
    selectedShapeTextParagraphs,
    ensureFamilyFacesLoaded,
    getVariantByFontId,
    getFamilyById,
    pickDefaultFontIdForFamilyId,
    defaultFontSize,
    resolvedDefaultFontFamilyId,
    textPanelRevision,
    // Edit enter/exit: the getters below never change identity, so this is what re-runs the sync
    // when a shape text edit ends and the panel must fall back to the selected text.
    shapeTextEditRevision,
    editSelectionTick,
    getEditingShapeTextId,
    getEditingShapeTextSelectionStyle,
    getEditingShapeTextKerningState,
    syncLeadingFromStoredValue,
    // Stable field methods (useCallback over setters/refs, or over deps already listed above):
    // listed for the linter, they never trigger a re-run on their own.
    syncFontSizeFromLiveEdit,
    showFontSizeSelection,
    resetFontSize,
    setUnderline,
    setAlignment,
    syncKerningFromLiveEdit,
    syncKerningFromParagraphs,
    resetKerning,
    setLiveEditingCollapsedCaret,
    syncTrackingFromLiveEdit,
    syncTrackingFromParagraphs,
    resetTracking,
    showFamilyDisplay,
    trackLeadingEditingSession,
    resyncLeadingFromLiveEdit,
    resyncLeadingFromParagraphs,
    clearLeading,
    applyDropCapSummary,
    resetDropCap,
  ])
}
